import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, MapPin, Scale, ShieldAlert, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";

const CLUSTERS = [
    {
        icon: Scale,
        title: "Muss ich das Pflegeheim meiner Eltern zahlen?",
        teaser: "Das Angehörigen-Entlastungsgesetz und die 100.000-€-Grenze – was das für Sie bedeutet.",
        tags: ["Elternunterhalt", "§ 94 SGB XII"],
    },
    {
        icon: MapPin,
        title: "Pflegeheimkosten in Baden-Württemberg",
        teaser: "Warum Eigenanteile in BW oft besonders hoch sind – und wie Sie sich orientieren.",
        tags: ["Eigenanteil", "Regionen"],
    },
    {
        icon: Users,
        title: "Müssen Geschwister gleich viel zahlen?",
        teaser: "Spoiler: Nein. Warum individuelle Verhältnisse zählen und was das praktisch bedeutet.",
        tags: ["Familie", "Leistungsfähigkeit"],
    },
    {
        icon: ShieldAlert,
        title: "Wenn die Rente nicht reicht",
        teaser: "Hilfe zur Pflege, Sozialamt, Vermögen – und wann Kinder überhaupt ins Spiel kommen.",
        tags: ["Sozialamt", "Rente"],
    },
    {
        icon: BookOpen,
        title: "Pflegegrad und Heimkosten",
        teaser: "Wie der Pflegegrad die Leistungen der Pflegekasse und den Eigenanteil beeinflusst.",
        tags: ["Pflegegrad", "Pflegekasse"],
    },
    {
        icon: Scale,
        title: "Vermögen im Pflegefall",
        teaser: "Schonvermögen, Freibeträge und was Sozialämter typischerweise prüfen.",
        tags: ["Vermögen", "Schonvermögen"],
    },
];

const GLOSSAR = [
    { term: "Angehörigen-Entlastungsgesetz", def: "Regelung aus 2020, die Kinder bis 100.000 € Jahresbrutto von Unterhaltsforderungen des Sozialamts grundsätzlich entlastet." },
    { term: "Eigenanteil", def: "Anteil der Heimkosten, den die pflegebedürftige Person selbst tragen muss – setzt sich aus mehreren Bausteinen zusammen." },
    { term: "Hilfe zur Pflege", def: "Sozialhilfeleistung nach SGB XII, wenn eigene Mittel und Pflegekassenleistungen nicht ausreichen." },
    { term: "Pflegegrad", def: "Von 1 bis 5. Bestimmt u. a. Leistungen der Pflegekasse und beeinflusst den Eigenanteil." },
    { term: "Schonvermögen", def: "Vermögen, das nicht für die Finanzierung herangezogen wird. Höhe und Umfang sind gesetzlich geregelt." },
];

export default function RatgeberPage() {
    return (
        <div className="py-12 sm:py-16">
            <Seo
                title="Ratgeber Pflegeheimkosten: Elternunterhalt, 100.000-€-Grenze, Eigenanteil"
                description="Strukturierte Themen zu Pflegeheimkosten für Angehörige: Elternunterhalt, Angehörigen-Entlastungsgesetz, Eigenanteile in Baden-Württemberg, Hilfe zur Pflege, Vermögen im Pflegefall. Glossar und Checkliste inklusive."
                path="/ratgeber"
            />
            <div className="container">
                <div className="max-w-2xl">
                    <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Wissen &amp; Ratgeber</div>
                    <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                        Klar erklärt: Pflegeheimkosten für Angehörige.
                    </h1>
                    <p className="mt-3 text-ink-500">
                        Strukturierte Themenbereiche, damit Sie Ihre Situation schnell einordnen können. Alle
                        Inhalte dienen der Orientierung – keine automatisierte Rechtsberatung.
                    </p>
                </div>

                {/* Clusters */}
                <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {CLUSTERS.map((c, i) => (
                        <article
                            key={c.title}
                            className="group rounded-3xl border border-ink-300/30 bg-white p-6 transition hover:-translate-y-1 hover:shadow-softer"
                            data-testid={`ratgeber-cluster-${i}`}
                        >
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                                <c.icon className="h-5 w-5" />
                            </div>
                            <h3 className="mt-3 font-display text-lg font-semibold">{c.title}</h3>
                            <p className="mt-2 text-sm text-ink-500">{c.teaser}</p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {c.tags.map((t) => (
                                    <span key={t} className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700">
                                        #{t}
                                    </span>
                                ))}
                            </div>
                            <Link to="/check" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-700">
                                Für meine Situation einordnen <ArrowRight className="h-4 w-4" />
                            </Link>
                        </article>
                    ))}
                </div>

                {/* Checkliste */}
                <div className="mt-16 rounded-3xl border border-brand-200 bg-brand-50/50 p-7 sm:p-10">
                    <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
                        <div className="max-w-xl">
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-700">
                                Checkliste
                            </div>
                            <h2 className="mt-1 font-display text-3xl font-bold">Was Sie jetzt vorbereiten sollten</h2>
                            <ul className="mt-4 space-y-2 text-sm text-ink-700">
                                <li>• Rentennachweise der pflegebedürftigen Person</li>
                                <li>• Heimvertrag (falls bereits im Heim)</li>
                                <li>• Übersicht über Vermögen und Konten</li>
                                <li>• Pflegegrad-Bescheid</li>
                                <li>• Schreiben von Sozialamt oder Pflegekasse</li>
                                <li>• Grobe Einkommensinformationen der Kinder</li>
                            </ul>
                        </div>
                        <Link to="/check" data-testid="ratgeber-cta">
                            <Button className="rounded-full bg-brand-500 hover:bg-brand-600">Kostenloser Check</Button>
                        </Link>
                    </div>
                </div>

                {/* Glossar */}
                <div className="mt-16">
                    <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Glossar</div>
                    <h2 className="mt-1 font-display text-3xl font-bold">Fachbegriffe verständlich erklärt</h2>
                    <dl className="mt-6 grid gap-4 md:grid-cols-2">
                        {GLOSSAR.map((g) => (
                            <div key={g.term} className="rounded-2xl border border-ink-300/30 bg-white p-5">
                                <dt className="font-display text-base font-semibold text-ink-900">{g.term}</dt>
                                <dd className="mt-1 text-sm text-ink-500">{g.def}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </div>
    );
}
